import { useState } from 'react';
import type { Cuestionario } from '@copathy/shared';
import { useNavigate } from 'react-router-dom';
import { Boton } from '../componentes/Boton';
import { Campo } from '../componentes/Campo';
import { Contenedor } from '../componentes/Contenedor';
import { Modal } from '../componentes/Modal';
import { BuscadorFiltros } from '../componentes/BuscadorFiltros';
import { ImagenCuestionario } from '../componentes/ImagenCuestionario';
import { Pestanas, type EstadoVisible } from '../componentes/Pestanas';
import { Lista } from '../componentes/iconos';
import { useMigas } from '../lib/migas';
import { usePuede } from '../lib/auth';
import { ErrorApi } from '../lib/api';
import { useCrearCuestionario, useCuestionarios } from '../lib/cuestionarios';

/**
 * M5 — la biblioteca de cuestionarios.
 *
 * ── Lo que esta pantalla tiene que dejar claro de un vistazo ──
 * No es «cuántos cuestionarios hay». Es **cuál está vigente y cuál tiene trabajo
 * a medias**. Un cuestionario con un borrador abierto es uno que alguien empezó
 * a cambiar y no terminó: si eso no se ve en la lista, se descubre el día que se
 * intenta publicar y aparece un conflicto que nadie esperaba.
 */
export function Cuestionarios() {
  const [texto, setTexto] = useState('');
  /*
    El estado vive en una PESTAÑA, no en el panel de filtros. Ahí adentro era un
    valor por omisión invisible: la lista escondía los desactivados y la razón
    estaba plegada a dos clics.
  */
  const [estado, setEstado] = useState<EstadoVisible>('si');
  /*
    Los valores de `BuscadorFiltros` son **cadenas o `undefined`**, no booleanos:
    viajan tal cual a la query de la URL. Una casilla marcada es una cadena
    presente, así que se lee con `Boolean(...)` y no comparando contra `true`.
  */
  const [filtros, setFiltros] = useState<Record<string, string | undefined>>({});
  const [creando, setCreando] = useState(false);
  const ir = useNavigate();

  useMigas([{ texto: 'Cuestionarios' }]);

  /*
    ⚠️ **Se piden TODOS y se reparte acá**, no uno por pestaña.

    Los contadores de las pestañas tienen que decir cuántos hay en cada una, y
    con la consulta filtrada por `activo` solo llegan los de la pestaña abierta:
    los otros dos contadores serían inventados. Pedir tres veces sería tres
    viajes para pintar dos números.

    Y así los contadores cuentan **dentro de la búsqueda**, que es lo que
    significan: «de lo que estás buscando, 4 activos y 1 desactivado».
  */
  const consulta = useCuestionarios({ texto, activo: 'todas' });
  const puedeArmar = usePuede('cuestionarios.armar');

  const todos = consulta.data ?? [];

  /*
    Los valores llegan como CSV en un campo —así entran tal cual en la query de
    la URL— así que un multi-select se lee partiéndolo. Un conjunto vacío
    significa **sin filtrar**, no «ninguno»: si no eligió nada, pasan todos.
  */
  const conjunto = (clave: string) =>
    new Set(
      (filtros[clave] ?? '')
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean),
    );

  const publicacion = conjunto('publicacion');
  const tamano = conjunto('tamano');
  const partes = conjunto('partes');
  const soloConBorrador = Boolean(filtros.conBorrador);
  const soloConPortada = Boolean(filtros.conPortada);

  const enPublicacion = (c: Cuestionario) => {
    if (publicacion.size === 0) return true;
    // «Publicado y con cambios» es publicado Y con borrador: es el estado que
    // más importa vigilar y el que en la lista son dos insignias separadas.
    if (publicacion.has('publicado') && c.versionPublicada !== null) return true;
    if (publicacion.has('sin_publicar') && c.versionPublicada === null) return true;
    if (publicacion.has('con_cambios') && c.versionPublicada !== null && c.tieneBorrador) {
      return true;
    }
    return false;
  };

  const enTamano = (c: Cuestionario) => {
    if (tamano.size === 0) return true;
    const n = c.preguntas;
    return (
      (tamano.has('vacio') && n === 0) ||
      (tamano.has('corto') && n >= 1 && n <= 10) ||
      (tamano.has('medio') && n >= 11 && n <= 25) ||
      (tamano.has('largo') && n > 25)
    );
  };

  /*
    ⚠️ «Repartido en páginas» se deduce de `paginas > 1`, y ese dato **viene del
    resumen de la versión vigente**, no de un conteo aparte: un cuestionario sin
    secciones sigue siendo UNA página (por eso el `greatest(…, 1)` de la
    consulta), así que «una sola página» es exactamente «sin secciones».
  */
  const enPartes = (c: Cuestionario) => {
    if (partes.size === 0) return true;
    const repartido = c.paginas > 1;
    return (partes.has('con_secciones') && repartido) || (partes.has('sin_secciones') && !repartido);
  };

  const enFechas = (c: Cuestionario) => {
    const desde = filtros.creadoDesde;
    const hasta = filtros.creadoHasta;
    // `creadoEn` es un ISO completo; se compara por los diez primeros
    // caracteres para que «hasta el 8» incluya todo el día 8 y no hasta su
    // medianoche.
    const dia = c.creadoEn.slice(0, 10);
    return (!desde || dia >= desde) && (!hasta || dia <= hasta);
  };

  const pasaFiltros = (c: Cuestionario) =>
    (!soloConBorrador || c.tieneBorrador) &&
    (!soloConPortada || c.tienePortada) &&
    enPublicacion(c) &&
    enTamano(c) &&
    enPartes(c) &&
    enFechas(c);

  const conFiltros = todos.filter(pasaFiltros);
  const activos = conFiltros.filter((c) => c.activo);
  const desactivados = conFiltros.filter((c) => !c.activo);
  const visibles = estado === 'si' ? activos : estado === 'no' ? desactivados : conFiltros;

  const pestanas = [
    { clave: 'si' as const, texto: 'Activos', n: activos.length },
    { clave: 'no' as const, texto: 'Desactivados', n: desactivados.length },
    { clave: 'todas' as const, texto: 'Todos', n: conFiltros.length },
  ];

  return (
    <Contenedor>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-marino-800">Cuestionarios</h1>
        <p className="mt-1 text-sm text-marino-400">
          Los instrumentos con los que COPATHY mide antes, durante y después de cada sesión.
        </p>
      </div>

      <div className="mb-5">
        <BuscadorFiltros
          busqueda={texto}
          alBuscar={setTexto}
          guia="Buscar por nombre o descripción"
          /*
            ── Por qué se filtra por esto y no por otra cosa ──
            `BuscadorFiltros` dibuja el botón solo si se le pasan filtros. La
            regla para elegirlos: **lo que la pestaña de estado no cubre y que en
            la lista solo se ve leyendo fila por fila**.

            Las dos casillas de arriba son las de un vistazo. Lo demás va en
            secciones porque son preguntas distintas: «en qué punto está» no es
            lo mismo que «qué tiene dentro».
          */
          casillas={[
            { clave: 'conBorrador', texto: 'Con borrador abierto' },
            { clave: 'conPortada', texto: 'Con portada' },
          ]}
          secciones={[
            {
              titulo: 'Publicación',
              campos: [
                {
                  tipo: 'multi',
                  clave: 'publicacion',
                  texto: 'En qué punto está',
                  opciones: [
                    { valor: 'publicado', texto: 'Publicado' },
                    { valor: 'sin_publicar', texto: 'Nunca publicado' },
                    { valor: 'con_cambios', texto: 'Publicado y con cambios sin publicar' },
                  ],
                },
              ],
            },
            {
              titulo: 'Contenido',
              campos: [
                {
                  tipo: 'multi',
                  clave: 'tamano',
                  texto: 'Cuántas preguntas',
                  opciones: [
                    { valor: 'vacio', texto: 'Ninguna todavía' },
                    { valor: 'corto', texto: 'Hasta 10' },
                    { valor: 'medio', texto: 'Entre 11 y 25' },
                    { valor: 'largo', texto: 'Más de 25' },
                  ],
                },
                {
                  tipo: 'multi',
                  clave: 'partes',
                  texto: 'Estructura',
                  opciones: [
                    { valor: 'con_secciones', texto: 'Repartido en páginas' },
                    { valor: 'sin_secciones', texto: 'Una sola página' },
                  ],
                },
              ],
            },
            {
              titulo: 'Creación',
              campos: [
                { tipo: 'fecha', claveDesde: 'creadoDesde', claveHasta: 'creadoHasta', texto: 'Creado' },
              ],
            },
          ]}
          valores={filtros}
          alAplicar={setFiltros}
          derecha={
            puedeArmar ? (
              <Boton onClick={() => setCreando(true)} className="py-3">
                Nuevo cuestionario
              </Boton>
            ) : undefined
          }
        />
      </div>

      <Pestanas etiqueta="Estado" pestanas={pestanas} activa={estado} alElegir={setEstado} />

      {consulta.isPending && <p className="py-16 text-center text-marino-400">Cargando…</p>}

      {consulta.isError && (
        <p className="py-16 text-center text-red-600">
          No pudimos cargar los cuestionarios. Recarga la página.
        </p>
      )}

      {!consulta.isPending && !consulta.isError && visibles.length === 0 && (
        <div className="rounded-2xl border border-marino-100 bg-white py-16 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-marino-50 text-marino-300">
            <Lista size={24} />
          </div>
          <h2 className="font-semibold text-marino-700">
            {texto ? 'Ningún cuestionario coincide' : 'Todavía no hay cuestionarios'}
          </h2>
          <p className="mt-1 text-sm text-marino-400">
            {texto
              ? 'Prueba con otro nombre, o quita algún filtro.'
              : 'Arma el primero: se guarda como borrador y solo se publica cuando esté listo.'}
          </p>
        </div>
      )}

      {/*
        ── Una fila por cuestionario, no dos columnas de tarjetas ──
        En dos columnas, el nombre —que es lo único por lo que se busca un
        instrumento— quedaba en la mitad del ancho y se cortaba, y el ojo tenía
        que recorrer en zigzag. En filas, los nombres se leen en columna y las
        insignias caen alineadas: comparar «cuál está publicado» pasa de leer
        ocho tarjetas a recorrer una columna.
      */}
      <ul className="space-y-2">
        {visibles.map((c) => (
          <li key={c.id}>
            <button
              type="button"
              onClick={() => ir(`/cuestionarios/${c.id}`)}
              className={`flex w-full flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border
                bg-white px-5 py-4 text-left transition
                hover:border-marino-300 hover:shadow-sm
                focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15
                ${c.activo ? 'border-marino-100' : 'border-marino-100 opacity-70'}`}
            >
              {/*
                La portada de miniatura, si la tiene. En una fila cabe sin
                empujar nada, y es lo que distingue de un vistazo un instrumento
                de otro cuando los nombres se parecen.
              */}
              {c.tienePortada && (
                <ImagenCuestionario
                  ruta={`/api/cuestionarios/${c.id}/portada`}
                  hay
                  alt=""
                  className="h-11 w-16 shrink-0 rounded-xl"
                />
              )}

              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2.5">
                  <span className="min-w-0 truncate font-semibold text-marino-800">{c.nombre}</span>
                  {!c.activo && (
                    <span className="shrink-0 rounded-full bg-marino-50 px-2.5 py-0.5 text-xs font-semibold text-marino-500">
                      Desactivado
                    </span>
                  )}
                </span>
                {c.descripcion && (
                  <span className="mt-0.5 block truncate text-sm text-marino-400">
                    {c.descripcion}
                  </span>
                )}
              </span>

              {/*
                Los dos estados que importan, y por qué son dos etiquetas y no
                una: un cuestionario puede estar publicado Y tener un borrador
                encima. Fundirlas en un solo rótulo obligaría a elegir cuál se
                pierde.
              */}
              <span className="flex shrink-0 flex-wrap items-center gap-2 text-xs font-semibold">
                {c.versionPublicada ? (
                  <span className="rounded-full bg-marino-700 px-2.5 py-1 text-white">
                    Publicado · v{c.versionPublicada}
                  </span>
                ) : (
                  <span className="rounded-full bg-marino-50 px-2.5 py-1 text-marino-500">
                    Sin publicar
                  </span>
                )}
                {c.tieneBorrador && (
                  <span className="rounded-full bg-naranja-100 px-2.5 py-1 text-marino-800">
                    Borrador abierto
                  </span>
                )}
                <span className="w-24 text-right text-marino-400">
                  {c.preguntas} {c.preguntas === 1 ? 'pregunta' : 'preguntas'}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>

      {creando && <ModalNuevo alCerrar={() => setCreando(false)} />}
    </Contenedor>
  );
}

function ModalNuevo({ alCerrar }: { alCerrar: () => void }) {
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [campos, setCampos] = useState<Record<string, string>>({});
  const crear = useCrearCuestionario();
  const ir = useNavigate();

  async function enviar() {
    setError(null);
    setCampos({});
    try {
      const c = await crear.mutateAsync({
        nombre: nombre.trim(),
        ...(descripcion.trim() ? { descripcion: descripcion.trim() } : {}),
      });
      // Se entra directo a armarlo: crear un cuestionario y quedarse en la lista
      // deja a la gente buscando en la lista lo que acaba de crear.
      ir(`/cuestionarios/${c.id}`);
    } catch (e) {
      if (e instanceof ErrorApi) {
        setError(e.message);
        setCampos(e.campos ?? {});
        return;
      }
      setError('No pudimos crear el cuestionario. Intenta de nuevo.');
    }
  }

  return (
    <Modal
      titulo="Nuevo cuestionario"
      ancho="medio"
      abierto
      hayCambios={nombre.trim().length > 0 || descripcion.trim().length > 0}
      alCerrar={alCerrar}
      pie={
        <>
          <Boton variante="secundario" onClick={alCerrar}>
            Cancelar
          </Boton>
          <Boton onClick={() => void enviar()} cargando={crear.isPending}>
            Crear y armar
          </Boton>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-marino-500">
          Nace como <strong className="font-semibold text-marino-700">borrador</strong>. Nadie lo
          responde hasta que lo publiques.
        </p>

        {error && (
          <div
            role="alert"
            className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          >
            {error}
          </div>
        )}

        <Campo
          etiqueta="Nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          error={campos.nombre}
          autoFocus
        />
        <Campo
          etiqueta="Descripción (opcional)"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          error={campos.descripcion}
        />
      </div>
    </Modal>
  );
}
